import { effect, inject } from '@angular/core';
import { CompanyType, Role } from '@app/models/admin.model';
import {
  BaseEnumResponse,
  BaseMasterDataResponse,
  LocalizedValue,
} from '@app/models/shared.models';
import { trackUnauthorizedLoad } from '@app/helpers/master-data-unauthorized-load.helper';
import { AuthStore } from '@app/stores/auth.store';
import {
  patchState,
  signalStore,
  signalStoreFeature,
  type,
  withHooks,
  withState,
} from '@ngrx/signals';
import {
  entityConfig,
  setEntities,
  withEntities,
} from '@ngrx/signals/entities';
import { forkJoin, Observable } from 'rxjs';
import { GenericDataService } from '@app/services/generic-data.service';

const languageConfig = entityConfig({
  entity: type<BaseMasterDataResponse>(),
  collection: 'language',
  selectId: (entity) => entity.code,
});

const roleConfig = entityConfig({
  entity: type<Role>(),
  collection: 'role',
  selectId: (role) => role.name,
});

const enumsTail = signalStoreFeature(
  withEntities({
    entity: type<BaseEnumResponse>(),
    collection: 'financialAssetType',
  }),
  withEntities({
    entity: type<BaseEnumResponse>(),
    collection: 'investmentStatus',
  }),
  withEntities({
    entity: type<BaseEnumResponse>(),
    collection: 'ownershipStructure',
  }),
);

type GenericLoadKey =
  | 'languages'
  | 'roles'
  | 'countries'
  | 'currencies'
  | 'nameTitles'
  | 'idCardTypes'
  | 'investorQualification'
  | 'userRepresentationTypes'
  | 'incompleteCollectionCase'
  | 'publicSpaceTypes'
  | 'companyTypes'
  | 'fileType'
  | 'financialAssetType'
  | 'investmentStatus'
  | 'ownershipStructure';

export const GenericStore = signalStore(
  { providedIn: 'root' },
  withState({ allLoaded: false }),
  withEntities(languageConfig),
  withEntities(roleConfig),
  withEntities({ entity: type<LocalizedValue>(), collection: 'countries' }),
  withEntities({ entity: type<LocalizedValue>(), collection: 'currencies' }),
  withEntities({ entity: type<BaseEnumResponse>(), collection: 'idCardType' }),
  withEntities({
    entity: type<BaseEnumResponse>(),
    collection: 'publicSpaceType',
  }),
  withEntities({
    entity: type<BaseEnumResponse>(),
    collection: 'investorQualification',
  }),
  withEntities({
    entity: type<BaseEnumResponse>(),
    collection: 'userRepresentationType',
  }),
  withEntities({
    entity: type<BaseEnumResponse>(),
    collection: 'incompleteCollectionCase',
  }),
  withEntities({ entity: type<LocalizedValue>(), collection: 'nameTitles' }),
  withEntities({ entity: type<CompanyType>(), collection: 'companyTypes' }),
  withEntities({ entity: type<BaseEnumResponse>(), collection: 'fileType' }),
  enumsTail,
  withHooks((store) => {
    const genericDataService = inject(GenericDataService);
    const authStore = inject(AuthStore);
    const pendingUnauthorizedLoads = new Set<GenericLoadKey>();

    const track = <T>(key: GenericLoadKey, empty: T) =>
      trackUnauthorizedLoad(key, pendingUnauthorizedLoads, empty);

    const buildLoadRequests = (
      keys: Iterable<GenericLoadKey>,
    ): Partial<Record<GenericLoadKey, Observable<unknown>>> => {
      const requests: Partial<Record<GenericLoadKey, Observable<unknown>>> = {};

      for (const key of keys) {
        switch (key) {
          case 'languages':
            requests.languages = genericDataService
              .loadLanguages()
              .pipe(track('languages', [] as BaseMasterDataResponse[]));
            break;
          case 'roles':
            requests.roles = genericDataService
              .loadRoles()
              .pipe(track('roles', [] as Role[]));
            break;
          case 'countries':
            requests.countries = genericDataService
              .loadCountries()
              .pipe(track('countries', { data: [] as LocalizedValue[] }));
            break;
          case 'currencies':
            requests.currencies = genericDataService
              .loadCurrencies()
              .pipe(track('currencies', { data: [] as LocalizedValue[] }));
            break;
          case 'nameTitles':
            requests.nameTitles = genericDataService
              .loadNameTitles()
              .pipe(track('nameTitles', { data: [] as LocalizedValue[] }));
            break;
          case 'idCardTypes':
            requests.idCardTypes = genericDataService
              .loadIdCardTypes()
              .pipe(track('idCardTypes', [] as BaseEnumResponse[]));
            break;
          case 'investorQualification':
            requests.investorQualification = genericDataService
              .loadInvestorQualification()
              .pipe(track('investorQualification', [] as BaseEnumResponse[]));
            break;
          case 'userRepresentationTypes':
            requests.userRepresentationTypes = genericDataService
              .loadUserRepresentationTypes()
              .pipe(
                track('userRepresentationTypes', [] as BaseEnumResponse[]),
              );
            break;
          case 'incompleteCollectionCase':
            requests.incompleteCollectionCase = genericDataService
              .loadIncompleteCollectionCase()
              .pipe(
                track('incompleteCollectionCase', [] as BaseEnumResponse[]),
              );
            break;
          case 'publicSpaceTypes':
            requests.publicSpaceTypes = genericDataService
              .loadPublicSpaceTypes()
              .pipe(track('publicSpaceTypes', [] as BaseEnumResponse[]));
            break;
          case 'companyTypes':
            requests.companyTypes = genericDataService
              .loadCompanyTypes()
              .pipe(track('companyTypes', { data: [] as CompanyType[] }));
            break;
          case 'fileType':
            requests.fileType = genericDataService
              .loadFileType()
              .pipe(track('fileType', [] as BaseEnumResponse[]));
            break;
          case 'financialAssetType':
            requests.financialAssetType = genericDataService
              .loadFinancialAssetType()
              .pipe(track('financialAssetType', [] as BaseEnumResponse[]));
            break;
          case 'investmentStatus':
            requests.investmentStatus = genericDataService
              .loadInvestmentStatus()
              .pipe(track('investmentStatus', [] as BaseEnumResponse[]));
            break;
          case 'ownershipStructure':
            requests.ownershipStructure = genericDataService
              .loadOwnershipStructure()
              .pipe(track('ownershipStructure', [] as BaseEnumResponse[]));
            break;
        }
      }

      return requests;
    };

    const applyLoadResults = (
      results: Partial<Record<GenericLoadKey, unknown>>,
    ): void => {
      const patches = [];

      if (results.languages) {
        patches.push(
          setEntities(
            results.languages as BaseMasterDataResponse[],
            languageConfig,
          ),
        );
      }
      if (results.roles) {
        patches.push(setEntities(results.roles as Role[], roleConfig));
      }
      if (results.countries) {
        patches.push(
          setEntities(
            (results.countries as { data: LocalizedValue[] }).data,
            { collection: 'countries' },
          ),
        );
      }
      if (results.currencies) {
        patches.push(
          setEntities(
            (results.currencies as { data: LocalizedValue[] }).data,
            { collection: 'currencies' },
          ),
        );
      }
      if (results.nameTitles) {
        patches.push(
          setEntities(
            (results.nameTitles as { data: LocalizedValue[] }).data,
            { collection: 'nameTitles' },
          ),
        );
      }
      if (results.idCardTypes) {
        patches.push(
          setEntities(results.idCardTypes as BaseEnumResponse[], {
            collection: 'idCardType',
          }),
        );
      }
      if (results.investorQualification) {
        patches.push(
          setEntities(results.investorQualification as BaseEnumResponse[], {
            collection: 'investorQualification',
          }),
        );
      }
      if (results.userRepresentationTypes) {
        patches.push(
          setEntities(
            results.userRepresentationTypes as BaseEnumResponse[],
            { collection: 'userRepresentationType' },
          ),
        );
      }
      if (results.incompleteCollectionCase) {
        patches.push(
          setEntities(
            results.incompleteCollectionCase as BaseEnumResponse[],
            { collection: 'incompleteCollectionCase' },
          ),
        );
      }
      if (results.publicSpaceTypes) {
        patches.push(
          setEntities(results.publicSpaceTypes as BaseEnumResponse[], {
            collection: 'publicSpaceType',
          }),
        );
      }
      if (results.companyTypes) {
        patches.push(
          setEntities(
            (results.companyTypes as { data: CompanyType[] }).data,
            { collection: 'companyTypes' },
          ),
        );
      }
      if (results.fileType) {
        patches.push(
          setEntities(results.fileType as BaseEnumResponse[], {
            collection: 'fileType',
          }),
        );
      }
      if (results.financialAssetType) {
        patches.push(
          setEntities(results.financialAssetType as BaseEnumResponse[], {
            collection: 'financialAssetType',
          }),
        );
      }
      if (results.investmentStatus) {
        patches.push(
          setEntities(results.investmentStatus as BaseEnumResponse[], {
            collection: 'investmentStatus',
          }),
        );
      }
      if (results.ownershipStructure) {
        patches.push(
          setEntities(results.ownershipStructure as BaseEnumResponse[], {
            collection: 'ownershipStructure',
          }),
        );
      }

      if (patches.length > 0) {
        patchState(store, ...patches);
      }
    };

    const allLoadKeys: GenericLoadKey[] = [
      'languages',
      'roles',
      'countries',
      'currencies',
      'nameTitles',
      'idCardTypes',
      'investorQualification',
      'userRepresentationTypes',
      'incompleteCollectionCase',
      'publicSpaceTypes',
      'companyTypes',
      'fileType',
      'financialAssetType',
      'investmentStatus',
      'ownershipStructure',
    ];

    const loadAll = (): void => {
      patchState(store, { allLoaded: false });

      forkJoin(buildLoadRequests(allLoadKeys)).subscribe((results) => {
        applyLoadResults(results);
        patchState(store, { allLoaded: true });
      });
    };

    const reloadUnauthorizedOnly = (): void => {
      if (pendingUnauthorizedLoads.size === 0) {
        return;
      }

      const keys = [...pendingUnauthorizedLoads];
      forkJoin(buildLoadRequests(keys)).subscribe((results) => {
        applyLoadResults(results);
      });
    };

    return {
      onInit() {
        loadAll();

        let wasLoggedIn = authStore.isLoggedIn();
        effect(() => {
          const loggedIn = authStore.isLoggedIn();
          if (loggedIn && !wasLoggedIn) {
            reloadUnauthorizedOnly();
          }
          wasLoggedIn = loggedIn;
        });
      },
    };
  }),
);
